import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  registerRestaurantApi, 
  loginRestaurantApi, 
  getProfileApi,
  updateProfileApi,
  updatePasswordApi,
  uploadProfileImageApi,
  deleteProfileImageApi,
  refreshTokenApi,
  logoutApi,
  // ========== NEW OTP APIS ==========
  forgotPasswordApi,
  verifyOTPApi,
  resetPasswordApi,
  resendOTPApi,
} from '../../api/authApi';


// =============================================
// FORGOT PASSWORD - OTP THUNKS (ADD THESE)
// =============================================

/**
 * Send OTP to email for password reset
 */
export const forgotPassword = createAsyncThunk(
  'user/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await forgotPasswordApi(email);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/**
 * Verify OTP
 */
export const verifyOTP = createAsyncThunk(
  'user/verifyOTP',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await verifyOTPApi(email, otp);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/**
 * Reset password with token
 */
export const resetPassword = createAsyncThunk(
  'user/resetPassword',
  async ({ resetToken, newPassword }, { rejectWithValue }) => {
    try {
      const response = await resetPasswordApi(resetToken, newPassword);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/**
 * Resend OTP
 */
export const resendOTP = createAsyncThunk(
  'user/resendOTP',
  async (email, { rejectWithValue }) => {
    try {
      const response = await resendOTPApi(email);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


// =============================================
// EXISTING THUNKS (Unchanged)
// =============================================

export const registerUser = createAsyncThunk(
  'user/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await registerRestaurantApi(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Registration failed");
    }
  }
);

export const loginUser = createAsyncThunk(
  'user/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await loginRestaurantApi(credentials);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Login failed");
    }
  }
);


export const getProfile = createAsyncThunk(
  'user/getProfile',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.user.token || localStorage.getItem('restaurantApp_token');
      
      if (!token) {
        throw new Error('No restaurantApp_token available');
      }
      
      const response = await getProfileApi();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to get profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'user/updateProfile',
  async (profileData, { rejectWithValue, getState }) => {
    try {
      const response = await updateProfileApi(profileData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updatePassword = createAsyncThunk(
  'user/updatePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await updatePasswordApi(passwordData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const uploadProfileImage = createAsyncThunk(
  'user/uploadProfileImage',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await uploadProfileImageApi(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteProfileImage = createAsyncThunk(
  'user/deleteProfileImage',
  async (_, { rejectWithValue }) => {
    try {
      const response = await deleteProfileImageApi();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const refreshToken = createAsyncThunk(
  'user/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await refreshTokenApi();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const logout = createAsyncThunk(
  'user/logout',
  async (_, { rejectWithValue }) => {
    try {
      const response = await logoutApi();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


// =============================================
// INITIAL STATE
// =============================================

const getInitialState = () => {
  const token = localStorage.getItem('restaurantApp_token');
  const refreshToken = localStorage.getItem('restaurantApp_refreshToken');
  let userStr = localStorage.getItem('restaurantApp_user') || localStorage.getItem('restaurant');
  
  let user = null;
  try {
    user = userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    user = null;
  }
  
  return {
    user,
    token,
    refreshToken,
    loading: false,
    error: null,
    isAuthenticated: !!token && !!user,
    profileLoading: false,
    profileError: null,
    // ========== NEW OTP STATE ==========
    forgotPassword: {
      loading: false,
      success: false,
      error: null,
      email: null,
      expiresAt: null,
    },
    verifyOTP: {
      loading: false,
      success: false,
      error: null,
      resetToken: null,
    },
    resetPassword: {
      loading: false,
      success: false,
      error: null,
    },
    resendOTP: {
      loading: false,
      success: false,
      error: null,
    },
    // ====================================
    operations: {
      profileUpdate: {
        loading: false,
        success: false,
        error: null
      },
      passwordUpdate: {
        loading: false,
        success: false,
        error: null
      },
      imageUpload: {
        loading: false,
        success: false,
        error: null
      },
      imageDelete: {
        loading: false,
        success: false,
        error: null
      }
    }
  };
};


// =============================================
// SLICE
// =============================================

const userSlice = createSlice({
  name: 'user',
  initialState: getInitialState(),
  reducers: {
    logoutSuccess: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.profileLoading = false;
      state.profileError = null;
      // Reset OTP states
      state.forgotPassword = { loading: false, success: false, error: null, email: null, expiresAt: null };
      state.verifyOTP = { loading: false, success: false, error: null, resetToken: null };
      state.resetPassword = { loading: false, success: false, error: null };
      state.resendOTP = { loading: false, success: false, error: null };
      state.operations = {
        profileUpdate: { loading: false, success: false, error: null },
        passwordUpdate: { loading: false, success: false, error: null },
        imageUpload: { loading: false, success: false, error: null },
        imageDelete: { loading: false, success: false, error: null }
      };
      
      localStorage.removeItem('restaurantApp_token');
      localStorage.removeItem('restaurantApp_refreshToken');
      localStorage.removeItem('restaurantApp_user');
      localStorage.removeItem('restaurant');
    },
    
    clearError: (state) => {
      state.error = null;
      state.profileError = null;
      state.forgotPassword.error = null;
      state.verifyOTP.error = null;
      state.resetPassword.error = null;
      state.resendOTP.error = null;
      state.operations.profileUpdate.error = null;
      state.operations.passwordUpdate.error = null;
      state.operations.imageUpload.error = null;
      state.operations.imageDelete.error = null;
    },
    
    // ========== OTP RESET REDUCERS ==========
    resetForgotPasswordState: (state) => {
      state.forgotPassword = { loading: false, success: false, error: null, email: null, expiresAt: null };
    },
    resetVerifyOTPState: (state) => {
      state.verifyOTP = { loading: false, success: false, error: null, resetToken: null };
    },
    resetResetPasswordState: (state) => {
      state.resetPassword = { loading: false, success: false, error: null };
    },
    resetResendOTPState: (state) => {
      state.resendOTP = { loading: false, success: false, error: null };
    },
    // =========================================
    
    resetProfileUpdate: (state) => {
      state.operations.profileUpdate = { loading: false, success: false, error: null };
    },
    
    resetPasswordUpdate: (state) => {
      state.operations.passwordUpdate = { loading: false, success: false, error: null };
    },
    
    resetImageUpload: (state) => {
      state.operations.imageUpload = { loading: false, success: false, error: null };
    },
    
    resetImageDelete: (state) => {
      state.operations.imageDelete = { loading: false, success: false, error: null };
    },
    
    setCredentials: (state, action) => {
      const { user, token, refreshToken } = action.payload;
      
      if (user) {
        state.user = user;
        localStorage.setItem('restaurantApp_user', JSON.stringify(user));
        localStorage.setItem('restaurant', JSON.stringify(user));
      }
      
      if (token) {
        state.token = token;
        localStorage.setItem('restaurantApp_token', token);
      }
      
      if (refreshToken) {
        state.refreshToken = refreshToken;
        localStorage.setItem('restaurantApp_refreshToken', refreshToken);
      }
      
      if (token && user) {
        state.isAuthenticated = true;
      }
      
      state.error = null;
    },
    
    initializeAuth: (state) => {
      const token = localStorage.getItem('restaurantApp_token');
      const userStr = localStorage.getItem('restaurantApp_user') || localStorage.getItem('restaurant');
      
      if (token && userStr) {
        try {
          state.token = token;
          state.user = JSON.parse(userStr);
          state.isAuthenticated = true;
        } catch (error) {
          console.error('Error initializing auth:', error);
          state.isAuthenticated = false;
        }
      }
    },
    
    updateUserData: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem('restaurantApp_user', JSON.stringify(state.user));
        localStorage.setItem('restaurant', JSON.stringify(state.user));
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // =============================================
      // FORGOT PASSWORD - OTP REDUCERS
      // =============================================
      
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPassword.loading = true;
        state.forgotPassword.success = false;
        state.forgotPassword.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.forgotPassword.loading = false;
        state.forgotPassword.success = true;
        state.forgotPassword.email = action.payload?.data?.email || action.payload?.email;
        state.forgotPassword.expiresAt = action.payload?.data?.expiresAt;
        state.forgotPassword.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPassword.loading = false;
        state.forgotPassword.success = false;
        state.forgotPassword.error = action.payload?.message || 'Failed to send OTP';
      })
      
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.verifyOTP.loading = true;
        state.verifyOTP.success = false;
        state.verifyOTP.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.verifyOTP.loading = false;
        state.verifyOTP.success = true;
        state.verifyOTP.resetToken = action.payload?.data?.resetToken;
        state.verifyOTP.error = null;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.verifyOTP.loading = false;
        state.verifyOTP.success = false;
        state.verifyOTP.error = action.payload?.message || 'OTP verification failed';
      })
      
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.resetPassword.loading = true;
        state.resetPassword.success = false;
        state.resetPassword.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetPassword.loading = false;
        state.resetPassword.success = true;
        state.resetPassword.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPassword.loading = false;
        state.resetPassword.success = false;
        state.resetPassword.error = action.payload?.message || 'Password reset failed';
      })
      
      // Resend OTP
      .addCase(resendOTP.pending, (state) => {
        state.resendOTP.loading = true;
        state.resendOTP.success = false;
        state.resendOTP.error = null;
      })
      .addCase(resendOTP.fulfilled, (state, action) => {
        state.resendOTP.loading = false;
        state.resendOTP.success = true;
        state.forgotPassword.expiresAt = action.payload?.data?.expiresAt;
        state.resendOTP.error = null;
      })
      .addCase(resendOTP.rejected, (state, action) => {
        state.resendOTP.loading = false;
        state.resendOTP.success = false;
        state.resendOTP.error = action.payload?.message || 'Failed to resend OTP';
      })
      
      // =============================================
      // EXISTING REDUCERS (Unchanged)
      // =============================================
      
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        const { user, token, refreshToken } = action.payload;
        
        if (user && token) {
          state.user = user;
          state.token = token;
          state.refreshToken = refreshToken || state.refreshToken;
          state.isAuthenticated = true;
          
          localStorage.setItem('restaurantApp_token', token);
          if (refreshToken) {
            localStorage.setItem('restaurantApp_refreshToken', refreshToken);
          }
          localStorage.setItem('restaurantApp_user', JSON.stringify(user));
          localStorage.setItem('restaurant', JSON.stringify(user));
        }
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Registration failed';
        state.isAuthenticated = false;
      })
      
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        const user = responseData.user || responseData.restaurant || responseData;
        const token = responseData.token || (responseData.user?.token) || state.token;
        const refreshToken = responseData.refreshToken;
        
        if (user && token) {
          state.user = user;
          state.token = token;
          state.refreshToken = refreshToken || state.refreshToken;
          state.isAuthenticated = true;
          
          localStorage.setItem('restaurantApp_token', token);
          if (refreshToken) {
            localStorage.setItem('restaurantApp_refreshToken', refreshToken);
          }
          localStorage.setItem('restaurantApp_user', JSON.stringify(user));
          localStorage.setItem('restaurant', JSON.stringify(user));
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Login failed';
        state.isAuthenticated = false;
      })
      
      // Get Profile
      .addCase(getProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profileError = null;
        
        const userData = action.payload.user || action.payload.data || action.payload;
        if (userData) {
          state.user = userData;
          localStorage.setItem('restaurantApp_user', JSON.stringify(userData));
          localStorage.setItem('restaurant', JSON.stringify(userData));
        }
        
        if (action.payload.token) {
          state.token = action.payload.token;
          localStorage.setItem('restaurantApp_token', action.payload.token);
        }
        
        state.isAuthenticated = true;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload?.message || 'Failed to get profile';
        
        if (state.token) {
          state.isAuthenticated = true;
        }
      })
      
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.operations.profileUpdate.loading = true;
        state.operations.profileUpdate.success = false;
        state.operations.profileUpdate.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.operations.profileUpdate.loading = false;
        state.operations.profileUpdate.success = true;
        
        const updatedUser = action.payload.user || action.payload.restaurant || action.payload;
        if (updatedUser) {
          state.user = updatedUser;
          localStorage.setItem('restaurantApp_user', JSON.stringify(updatedUser));
          localStorage.setItem('restaurant', JSON.stringify(updatedUser));
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.operations.profileUpdate.loading = false;
        state.operations.profileUpdate.error = action.payload?.message || 'Profile update failed';
      })
      
      // Update Password
      .addCase(updatePassword.pending, (state) => {
        state.operations.passwordUpdate.loading = true;
        state.operations.passwordUpdate.success = false;
        state.operations.passwordUpdate.error = null;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.operations.passwordUpdate.loading = false;
        state.operations.passwordUpdate.success = true;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.operations.passwordUpdate.loading = false;
        state.operations.passwordUpdate.error = action.payload?.message || 'Password update failed';
      })
      
      // Upload Profile Image
      .addCase(uploadProfileImage.pending, (state) => {
        state.operations.imageUpload.loading = true;
        state.operations.imageUpload.success = false;
        state.operations.imageUpload.error = null;
      })
      .addCase(uploadProfileImage.fulfilled, (state, action) => {
        state.operations.imageUpload.loading = false;
        state.operations.imageUpload.success = true;
        
        if (action.payload.user) {
          state.user = action.payload.user;
          localStorage.setItem('restaurantApp_user', JSON.stringify(action.payload.user));
          localStorage.setItem('restaurant', JSON.stringify(action.payload.user));
        }
      })
      .addCase(uploadProfileImage.rejected, (state, action) => {
        state.operations.imageUpload.loading = false;
        state.operations.imageUpload.error = action.payload?.message || 'Image upload failed';
      })
      
      // Delete Profile Image
      .addCase(deleteProfileImage.pending, (state) => {
        state.operations.imageDelete.loading = true;
        state.operations.imageDelete.success = false;
        state.operations.imageDelete.error = null;
      })
      .addCase(deleteProfileImage.fulfilled, (state, action) => {
        state.operations.imageDelete.loading = false;
        state.operations.imageDelete.success = true;
        
        if (action.payload.user) {
          state.user = action.payload.user;
          localStorage.setItem('restaurantApp_user', JSON.stringify(action.payload.user));
          localStorage.setItem('restaurant', JSON.stringify(action.payload.user));
        } else if (state.user) {
          state.user.profileImage = null;
          localStorage.setItem('restaurantApp_user', JSON.stringify(state.user));
          localStorage.setItem('restaurant', JSON.stringify(state.user));
        }
      })
      .addCase(deleteProfileImage.rejected, (state, action) => {
        state.operations.imageDelete.loading = false;
        state.operations.imageDelete.error = action.payload?.message || 'Image deletion failed';
      })
      
      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        if (action.payload.token) {
          state.token = action.payload.token;
          localStorage.setItem('restaurantApp_token', action.payload.token);
        }
      })
      
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
        state.profileLoading = false;
        state.profileError = null;
        state.forgotPassword = { loading: false, success: false, error: null, email: null, expiresAt: null };
        state.verifyOTP = { loading: false, success: false, error: null, resetToken: null };
        state.resetPassword = { loading: false, success: false, error: null };
        state.resendOTP = { loading: false, success: false, error: null };
        state.operations = {
          profileUpdate: { loading: false, success: false, error: null },
          passwordUpdate: { loading: false, success: false, error: null },
          imageUpload: { loading: false, success: false, error: null },
          imageDelete: { loading: false, success: false, error: null }
        };
        
        localStorage.removeItem('restaurantApp_token');
        localStorage.removeItem('restaurantApp_refreshToken');
        localStorage.removeItem('restaurantApp_user');
        localStorage.removeItem('restaurant');
      });
  },
});

// Export actions
export const { 
  logoutSuccess,
  clearError, 
  setCredentials, 
  initializeAuth,
  updateUserData,
  resetProfileUpdate,
  resetPasswordUpdate,
  resetImageUpload,
  resetImageDelete,
  // ========== NEW OTP ACTIONS ==========
  resetForgotPasswordState,
  resetVerifyOTPState,
  resetResetPasswordState,
  resetResendOTPState,
} = userSlice.actions;

// Selectors
export const selectUser = (state) => state.user.user;
export const selectToken = (state) => state.user.token;
export const selectRefreshToken = (state) => state.user.refreshToken;
export const selectIsAuthenticated = (state) => state.user.isAuthenticated;
export const selectLoading = (state) => state.user.loading;
export const selectError = (state) => state.user.error;
export const selectProfileLoading = (state) => state.user.profileLoading;
export const selectProfileError = (state) => state.user.profileError;
export const selectOperations = (state) => state.user.operations;

// ========== NEW OTP SELECTORS ==========
export const selectForgotPassword = (state) => state.user.forgotPassword;
export const selectVerifyOTP = (state) => state.user.verifyOTP;
export const selectResetPassword = (state) => state.user.resetPassword;
export const selectResendOTP = (state) => state.user.resendOTP;

// Helper selectors for operations
export const selectProfileUpdateOperation = (state) => state.user.operations.profileUpdate;
export const selectPasswordUpdateOperation = (state) => state.user.operations.passwordUpdate;
export const selectImageUploadOperation = (state) => state.user.operations.imageUpload;
export const selectImageDeleteOperation = (state) => state.user.operations.imageDelete;

export default userSlice.reducer;